import type { PlayerBadgeDto } from '@rw/shared';
import { Sheet } from '../../components/Sheet';
import { useT, locale } from '../../i18n';
import { BadgeMedal } from './BadgeMedal';
import { BADGE_INFO, TIER_NAMES, badgeName, badgeValue, isMultiTier } from './achievements';

const pct = (share: number) =>
  share >= 0.995 ? '100%' : share < 0.01 && share > 0 ? '<1%' : `${Math.round(share * 100)}%`;

/** One badge: every tier with its goal, when it was earned and how many runners have it. */
export function BadgeSheet({
  badge,
  onClose,
  isMe,
}: {
  badge: PlayerBadgeDto | null;
  onClose: () => void;
  isMe: boolean;
}) {
  const t = useT();
  if (!badge) return null;
  const info = BADGE_INFO[badge.id];
  const multi = isMultiTier(badge.id);
  return (
    <Sheet open onClose={onClose} label={badgeName(badge.id)}>
      <div data-testid="badge-sheet">
        <div className="flex flex-col items-center text-center">
          <BadgeMedal id={badge.id} tier={badge.tier} size={96} />
          <h2 className="mt-2 font-display text-2xl font-bold">{badgeName(badge.id)}</h2>
          <p className="text-sm text-ink-400">
            {isMe ? t('You') : t('Now')}: {badgeValue(badge.id, badge.progress)}
          </p>
        </div>
        <ul className="mt-4 space-y-2">
          {badge.thresholds.map((need, i) => {
            const tier = i + 1;
            const earned = badge.earned.find((e) => e.tier === tier);
            const ratio = Math.min(1, badge.progress / need);
            return (
              <li
                key={tier}
                className={`rounded-2xl border px-4 py-3 ${earned ? 'border-ink-600 bg-ink-850' : 'border-ink-800 bg-ink-900'}`}
              >
                <div className="flex items-center gap-3">
                  <BadgeMedal id={badge.id} tier={earned ? tier : 0} size={36} />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold">
                      {multi ? t(TIER_NAMES[i]!) : badgeName(badge.id)}
                      {earned ? (
                        <span className="ml-2 text-xs font-normal text-volt">
                          ✓ {new Date(earned.earnedAt).toLocaleDateString(locale())}
                        </span>
                      ) : null}
                    </p>
                    <p className="text-xs text-ink-400">{info.goal(need)}</p>
                  </div>
                  <span className="text-xs text-ink-400" title={t('Runners who have it')}>
                    {pct(badge.holders[i] ?? 0)}
                  </span>
                </div>
                {!earned ? (
                  <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-ink-700">
                    <div
                      className="h-full rounded-full bg-ink-400"
                      style={{ width: `${ratio * 100}%` }}
                    />
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
        <p className="mt-3 text-center text-xs text-ink-500">
          {t('% = share of active runners who have it')}
        </p>
      </div>
    </Sheet>
  );
}
